import React from "react";
import {
  Eye, Skull, VenetianMask, Crown, Lock, Key, Ghost, Compass, Anchor, Brain,
  Flame, Zap, Moon, Telescope, Scale, ScrollText, Feather, Radio, Sparkles,
  Infinity as InfinityIcon, Atom, Snowflake, ShieldAlert, ShieldCheck, Swords,
  Target, Fingerprint, UserCog, Spade, Gavel, Wand2, HelpCircle,
} from "lucide-react";

// Map of avatar_id -> Lucide icon. Must stay in sync with AVATAR_CATALOG in
// /app/backend/community_routes.py.
export const AVATAR_ICONS = {
  "eye": Eye,
  "skull": Skull,
  "venetian-mask": VenetianMask,
  "crown": Crown,
  "lock": Lock,
  "key": Key,
  "ghost": Ghost,
  "compass": Compass,
  "anchor": Anchor,
  "brain": Brain,
  "flame": Flame,
  "zap": Zap,
  "moon": Moon,
  "telescope": Telescope,
  "scale": Scale,
  "scroll": ScrollText,
  "feather": Feather,
  "radio": Radio,
  "sparkles": Sparkles,
  "infinity": InfinityIcon,
  "atom": Atom,
  "snowflake": Snowflake,
  "shield-alert": ShieldAlert,
  "shield-check": ShieldCheck,
  "swords": Swords,
  "target": Target,
  "fingerprint": Fingerprint,
  "spy": UserCog,
  "spade": Spade,
  "gavel": Gavel,
  "wand": Wand2,
  "question-mark": HelpCircle,
};

// Intrinsic color per avatar — mirrors AVATAR_CATALOG in
// /app/backend/community_routes.py so the icon always renders in its
// thematic color regardless of the parent text-color. Callers can still
// override via the `color` prop when an explicit color is required (e.g.,
// monochrome contexts).
export const AVATAR_COLORS = {
  "eye": "#ffd60a",
  "skull": "#ec4899",
  "venetian-mask": "#a78bfa",
  "crown": "#ffd60a",
  "lock": "#94a3b8",
  "key": "#ffd60a",
  "ghost": "#94a3b8",
  "compass": "#00ff88",
  "anchor": "#60a5fa",
  "brain": "#00ff88",
  "flame": "#ff3b30",
  "zap": "#ffd60a",
  "moon": "#60a5fa",
  "telescope": "#ffd60a",
  "scale": "#60a5fa",
  "scroll": "#ff9500",
  "feather": "#a78bfa",
  "radio": "#a78bfa",
  "sparkles": "#ffd60a",
  "infinity": "#a78bfa",
  "atom": "#00ff88",
  "snowflake": "#60a5fa",
  "shield-alert": "#ffaa00",
  "shield-check": "#a78bfa",
  "swords": "#ff3b30",
  "target": "#ff9500",
  "fingerprint": "#ec4899",
  "spy": "#94a3b8",
  "spade": "#ec4899",
  "gavel": "#ff9500",
  "wand": "#a78bfa",
  "question-mark": "#94a3b8",
};

/**
 * AvatarDisplay — renders the picked icon for a user, or falls back to the
 * 2-letter initial. Used in the Vault ID card, navbar, and public profile.
 *
 * Props:
 *  - avatarId: string|null — one of AVATAR_ICONS keys (from /me/avatars)
 *  - username: string — used for fallback initials and accessibility
 *  - size: number — px size of the icon
 *  - color: string — color of the icon when shown (defaults to currentColor)
 *  - className: string — additional classes for the container
 */
export default function AvatarDisplay({
  avatarId,
  aiAvatarId = null,
  username = "",
  size = 32,
  color,
  className = "",
}) {
  // AI Nano Banana avatar takes priority over the legacy icon avatar — when
  // a user picks one in Theme Studio it should follow them everywhere
  // AvatarDisplay is used (navbar pill, threads, comments, profile chip).
  if (aiAvatarId === "custom" && username) {
    // SENTINEL: "custom" means the user picked their OneTimeAssetGenerator
    // avatar. Serve from the by-username endpoint so threads/comments don't
    // need to ship the URL alongside the avatar id.
    const v = Math.floor(Date.now() / 3600000);
    const mediaUrl = `${process.env.REACT_APP_BACKEND_URL}/api/profile/custom-avatar/by-username/${encodeURIComponent(username)}/art.png?v=${v}`;
    return (
      <span
        className={`inline-flex items-center justify-center overflow-hidden rounded-full bg-[#0F0F0F] ${className}`}
        data-testid="avatar-display"
        data-avatar-id="custom"
        data-avatar-kind="custom"
        style={{ width: size, height: size, border: "1px solid rgba(34,211,238,0.45)" }}
        aria-label={`${username} custom avatar`}
      >
        <img
          src={mediaUrl}
          alt={`${username} avatar`}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      </span>
    );
  }
  if (aiAvatarId === "doxx" && username) {
    // SENTINEL: "doxx" means use the user's minted Doxx Card image as their
    // avatar everywhere AvatarDisplay renders.
    const v = Math.floor(Date.now() / 3600000);
    const mediaUrl = `${process.env.REACT_APP_BACKEND_URL}/api/profile/doxx-card/by-username/${encodeURIComponent(username)}/art.png?v=${v}`;
    return (
      <span
        className={`inline-flex items-center justify-center overflow-hidden rounded-full bg-[#0F0F0F] ${className}`}
        data-testid="avatar-display"
        data-avatar-id="doxx"
        data-avatar-kind="doxx"
        style={{ width: size, height: size, border: "1px solid rgba(255,59,48,0.55)" }}
        aria-label={`${username} doxx avatar`}
      >
        <img
          src={mediaUrl}
          alt={`${username} avatar`}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      </span>
    );
  }
  if (aiAvatarId) {
    // Soft cache-bust: hour-bucketed query stamp. Older browsers that
    // pinned the old `immutable, max-age=1yr` response to this URL will
    // see a different URL every hour and re-fetch. Backend now serves
    // `max-age=300, must-revalidate` + ETag so subsequent revalidations
    // are cheap (304 Not Modified).
    const v = Math.floor(Date.now() / 3600000);
    const mediaUrl = `${process.env.REACT_APP_BACKEND_URL}/api/avatars/${encodeURIComponent(aiAvatarId)}/media?generate=false&v=${v}`;
    return (
      <span
        className={`inline-flex items-center justify-center overflow-hidden rounded-full bg-[#0F0F0F] ${className}`}
        data-testid="avatar-display"
        data-avatar-id={aiAvatarId}
        data-avatar-kind="ai"
        style={{ width: size, height: size, border: "1px solid rgba(167,139,250,0.45)" }}
        aria-label={`${username} avatar`}
      >
        <img
          src={mediaUrl}
          alt={`${username} avatar`}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      </span>
    );
  }

  // Special case: "doxx" means the user has minted a Doxx Card and set it
  // as their avatar. Render the AI-illustrated card image as a circle.
  if (avatarId === "doxx" && username) {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/profile/doxx-card/by-username/${encodeURIComponent(username)}/art.png`;
    return (
      <span
        className={`inline-flex items-center justify-center overflow-hidden rounded-full bg-[#0F0F0F] ${className}`}
        data-testid="avatar-display"
        data-avatar-id="doxx"
        style={{ width: size, height: size, border: "1px solid rgba(255,59,48,0.4)" }}
        aria-label={`${username} doxx card avatar`}
      >
        <img
          src={url}
          alt={`${username} avatar`}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </span>
    );
  }

  const Icon = avatarId ? AVATAR_ICONS[avatarId] : null;
  // Auto-color the icon by its intrinsic theme color unless the caller explicitly
  // passed a `color` prop (e.g., monochrome share-cards). This stops the icon from
  // inheriting whatever text-color its parent wears (navbar pill, etc.).
  const intrinsicColor = avatarId ? AVATAR_COLORS[avatarId] : null;
  const renderColor = color || intrinsicColor || "currentColor";
  const initials = (username || "??").slice(0, 2).toUpperCase();
  return (
    <span
      className={`inline-flex items-center justify-center ${className}`}
      data-testid="avatar-display"
      data-avatar-id={avatarId || "initials"}
      aria-label={avatarId ? `${avatarId} avatar` : `${username} initials`}
    >
      {Icon ? (
        <Icon size={size} color={renderColor} strokeWidth={1.6} />
      ) : (
        <span style={color ? { color } : undefined}>{initials}</span>
      )}
    </span>
  );
}
